import { Box, Stack } from "@mui/material";
import { StatCardTypography } from "Components/UI/StatCard";
import { memo, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { useSelectedProject } from "Hooks";
import {
  ApiChartHaving,
  ApiChartRank,
  ApiChartTemplate,
  ApiChartType,
  ApiDimension,
  ApiMetric,
  ApiProduct,
} from "@incendium/api";
import {
  AnalyticsStatCard,
  biggestRegressionByMetric,
  mostImprovedByMetric,
  percentageChangeByRowByMetric,
  useFormatMetric,
} from "features/analytics";
import { ExploreConfig } from "features/explorer/types";
import { ExplorerTable } from "features/explorer";
import { AnalyticsSpacing } from "consts";

interface IProductRankingInnerProps {
  config: ExploreConfig;
  dimension: ApiDimension;
  products: ApiProduct[];
  ranks: ApiChartRank[];
  having: ApiChartHaving[];
}

function ProductRankingInner({
  config,
  dimension,
  products,
  ranks,
  having,
}: IProductRankingInnerProps) {
  const history = useHistory();
  const formatMetric = useFormatMetric();
  const { selectedProject, selectedClient } = useSelectedProject();

  const onClick = useCallback(
    (v: string) => {
      switch (dimension) {
        case ApiDimension.DIMENSION_PRODUCT_NAME:
          history.push(
            `/clients/${selectedClient?.id}/projects/${selectedProject?.id}/analyse/standard-reports/product-deep-dive`,
            {
              selectedProductId: products.find((p) => p?.name === v)?.id,
            },
          );
          break;
        case ApiDimension.DIMENSION_PRODUCT_CATEGORY:
          history.push(
            `/clients/${selectedClient?.id}/projects/${selectedProject?.id}/analyse/standard-reports/product-category-deep-dive`,
            {
              selectedProductCategory: v,
            },
          );
          break;

        default:
          break;
      }
    },
    [history, selectedClient, selectedProject, dimension, products],
  );

  const buildStatChartByMetric = useCallback(
    (metric?: ApiMetric) => {
      return {
        name: "",
        dimension: [dimension],
        template: ApiChartTemplate.SNAPSHOT,
        type: ApiChartType.GRAPH,
        attributes: [],
        ranks,
        having,
        includeEmpty: false,
        yAxisKeys: [
          {
            key: "l",
            fields: metric
              ? [metric, ApiMetric.METRIC_RANK]
              : [ApiMetric.METRIC_PRODUCT_REVENUE, ApiMetric.METRIC_RANK],
          },
        ],
      };
    },
    [dimension, ranks, having],
  );

  return (
    <Stack sx={{ height: "100%" }}>
      {config.cards && (
        <>
          <Stack direction="row" spacing={AnalyticsSpacing}>
            {config.cards.includes("improvement") && (
              <AnalyticsStatCard
                height={130}
                chart={buildStatChartByMetric()}
                forceComparison
                renderBody={(data, compData) => {
                  const mostImproved = mostImprovedByMetric(
                    data,
                    compData || [],
                    ApiMetric.METRIC_RANK,
                  );

                  return (
                    <>
                      <StatCardTypography size="small" mb={1}>
                        Most Improved Product
                      </StatCardTypography>
                      <StatCardTypography size="medium" lines={1} mb={1}>
                        {mostImproved.name}
                      </StatCardTypography>
                    </>
                  );
                }}
              />
            )}
            {config.cards.includes("ltv") && (
              <AnalyticsStatCard
                height={130}
                chart={buildStatChartByMetric(
                  ApiMetric.METRIC_PRODUCT_GATEWAY_LTV,
                )}
                renderBody={(data) => {
                  return (
                    <>
                      <StatCardTypography size="small" mb={1}>
                        Highest LTV
                      </StatCardTypography>
                      <StatCardTypography size="medium" lines={1} mb={1}>
                        {data[0]?.name}
                      </StatCardTypography>
                    </>
                  );
                }}
              />
            )}
            {config.cards.includes("top") && (
              <AnalyticsStatCard
                height={130}
                chart={buildStatChartByMetric(
                  ApiMetric.METRIC_PRODUCT_CLICK_THROUGH_RATE,
                )}
                renderBody={(data) => {
                  if (!data[0]) {
                    return;
                  }
                  return (
                    <>
                      <StatCardTypography size="small" mb={1}>
                        Top Performer in CTR
                      </StatCardTypography>
                      <StatCardTypography size="medium" lines={1} mb={1}>
                        {data[0]?.name}
                      </StatCardTypography>
                      <StatCardTypography size="small">
                        {formatMetric(
                          ApiMetric.METRIC_PRODUCT_CLICK_THROUGH_RATE,
                          Number(
                            data[0][
                              ApiMetric.METRIC_PRODUCT_CLICK_THROUGH_RATE
                            ],
                          ),
                        )}
                      </StatCardTypography>
                    </>
                  );
                }}
              />
            )}
            {config.cards.includes("top") && (
              <AnalyticsStatCard
                height={130}
                chart={buildStatChartByMetric(ApiMetric.METRIC_PRODUCT_REVENUE)}
                forceComparison
                renderBody={(data, compData) => {
                  const mostImproved = mostImprovedByMetric(
                    data,
                    compData || [],
                    ApiMetric.METRIC_PRODUCT_REVENUE,
                  );

                  return (
                    <>
                      <StatCardTypography size="small" mb={1}>
                        Trending Product
                      </StatCardTypography>
                      <StatCardTypography size="medium" lines={1} mb={1}>
                        {mostImproved.name}
                      </StatCardTypography>
                      <StatCardTypography size="small">
                        <Stack direction="row" alignItems={"center"}>
                          {formatMetric(
                            ApiMetric.METRIC_PRODUCT_REVENUE,
                            Number(
                              mostImproved[ApiMetric.METRIC_PRODUCT_REVENUE],
                            ),
                          )}
                        </Stack>
                      </StatCardTypography>
                    </>
                  );
                }}
              />
            )}

            {config.cards.includes("regression") && (
              <AnalyticsStatCard
                height={130}
                direction={"down"}
                chart={buildStatChartByMetric()}
                forceComparison
                setDirection={(data, compData) =>
                  percentageChangeByRowByMetric(
                    biggestRegressionByMetric(
                      data,
                      compData || [],
                      ApiMetric.METRIC_RANK,
                    ),
                    compData || [],
                    ApiMetric.METRIC_RANK,
                  ) > 0
                    ? "up"
                    : "down"
                }
                renderBody={(data, compData) => {
                  const regression = biggestRegressionByMetric(
                    data,
                    compData || [],
                    ApiMetric.METRIC_RANK,
                  );
                  return (
                    <>
                      <StatCardTypography size="small" mb={1}>
                        Biggest Regression
                      </StatCardTypography>
                      <StatCardTypography size="medium" lines={1} mb={1}>
                        {regression.name}
                      </StatCardTypography>
                      <StatCardTypography size="small">
                        {formatMetric(
                          ApiMetric.METRIC_RANK,
                          Number(data?.[0][ApiMetric.METRIC_RANK]) || 0,
                        )}
                      </StatCardTypography>
                    </>
                  );
                }}
              />
            )}
            {config.cards.includes("bottom") && (
              <AnalyticsStatCard
                height={130}
                chart={buildStatChartByMetric(
                  ApiMetric.METRIC_PRODUCT_CLICK_THROUGH_RATE,
                )}
                renderBody={(data) => {
                  return (
                    <>
                      <StatCardTypography size="small" mb={1}>
                        Worst Performer in CTR
                      </StatCardTypography>
                      <StatCardTypography size="medium" lines={1} mb={1}>
                        {data[0].name}
                      </StatCardTypography>
                      <StatCardTypography size="small">
                        {formatMetric(
                          ApiMetric.METRIC_PRODUCT_CLICK_THROUGH_RATE,
                          Number(
                            data[0][
                              ApiMetric.METRIC_PRODUCT_CLICK_THROUGH_RATE
                            ],
                          ) || 0,
                        )}
                      </StatCardTypography>
                    </>
                  );
                }}
              />
            )}
            {config.cards.includes("cold") && (
              <AnalyticsStatCard
                height={130}
                chart={buildStatChartByMetric(ApiMetric.METRIC_PRODUCT_REVENUE)}
                forceComparison
                renderBody={(data, compData) => {
                  const regression = biggestRegressionByMetric(
                    data,
                    compData || [],
                    ApiMetric.METRIC_PRODUCT_REVENUE,
                  );

                  return (
                    <>
                      <StatCardTypography size="small" mb={1}>
                        Cold Product
                      </StatCardTypography>
                      <StatCardTypography size="medium" lines={1} mb={1}>
                        {regression.name}
                      </StatCardTypography>
                      <StatCardTypography size="small">
                        <Stack direction="row" alignItems={"center"}>
                          {formatMetric(
                            ApiMetric.METRIC_PRODUCT_REVENUE,
                            Number(
                              regression[ApiMetric.METRIC_PRODUCT_REVENUE],
                            ),
                          )}
                        </Stack>
                      </StatCardTypography>
                    </>
                  );
                }}
              />
            )}
          </Stack>
        </>
      )}

      <Box mt={2} sx={{ flex: 1 }}>
        <ExplorerTable
          onClick={{ [dimension]: onClick }}
          metrics={[
            ApiMetric.METRIC_PRODUCT_PAGEVIEWS_COUNT,
            ApiMetric.METRIC_SESSIONS_COUNT,
            ApiMetric.METRIC_AVERAGE_TIME_ON_PAGE,
            ApiMetric.METRIC_PRODUCT_IMPRESSIONS_COUNT,
            ApiMetric.METRIC_PRODUCT_IMPRESSION_RATE,
            ApiMetric.METRIC_PRODUCT_CLICK_THROUGH_COUNT,
            ApiMetric.METRIC_PRODUCT_CLICK_THROUGH_RATE,
            ApiMetric.METRIC_PAGE_ADD_TO_CART_CLICK_COUNT,
            ApiMetric.METRIC_PAGE_ADD_TO_CART_CLICK_RATE,
            ApiMetric.METRIC_SALES_COMPLETION_RATE,
            ApiMetric.METRIC_PRODUCT_REVENUE,
            ApiMetric.METRIC_SALES_REVENUE,

            ApiMetric.METRIC_VISIT_TO_PURCHASE_RATE,
            ApiMetric.METRIC_PRODUCT_GATEWAY_LTV,
            ApiMetric.METRIC_RANK,
          ]}
        />
      </Box>
    </Stack>
  );
}

export default memo(ProductRankingInner);
